import { ChevronLeft, ChevronRight } from 'lucide-react';
import ReactCalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';

import type { AnalyticsCalendarDayDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';

interface CalendarHeatmapProps {
  days: AnalyticsCalendarDayDto[] | undefined;
  currency: string;
  year: number;
  maxYear: number;
  onYearChange: (year: number) => void;
  onDaySelect: (date: string) => void;
}

function classForPnl(pnl: number): string {
  const magnitude = Math.abs(pnl);
  const intensity = magnitude >= 5_000 ? 4 : magnitude >= 1_000 ? 3 : magnitude >= 250 ? 2 : 1;
  if (pnl > 0) return `dt-heat-profit-${intensity} dt-heat-clickable`;
  if (pnl < 0) return `dt-heat-loss-${intensity} dt-heat-clickable`;
  return 'dt-heat-flat dt-heat-clickable';
}

export function CalendarHeatmap({
  currency,
  days,
  maxYear,
  onDaySelect,
  onYearChange,
  year,
}: CalendarHeatmapProps) {
  const values = (days ?? []).map((day) => ({
    date: day.date,
    pnl: day.pnl,
    tradeCount: day.tradeCount,
  }));

  return (
    <section className="rounded border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Calendar</h2>
          <p className="text-sm text-text-muted">Click a day to see its closed lots.</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onYearChange(year - 1)}
            className="rounded p-2 text-text-muted hover:bg-surface-raised hover:text-text"
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="min-w-[3rem] text-center text-sm tabular-nums" aria-live="polite">
            {year}
          </span>
          <button
            type="button"
            onClick={() => onYearChange(year + 1)}
            disabled={year >= maxYear}
            className="rounded p-2 text-text-muted hover:bg-surface-raised hover:text-text disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div
        className="mt-4 overflow-x-auto pb-2"
        role="img"
        aria-label={`Daily realised P&L heatmap for ${year}. Use the monthly tiles below for keyboard access.`}
      >
        <div className="min-w-[720px]">
          <ReactCalendarHeatmap
            startDate={new Date(Date.UTC(year, 0, 1))}
            endDate={new Date(Date.UTC(year, 11, 31))}
            values={values}
            classForValue={(value) => {
              if (!value) return 'dt-heat-empty';
              return classForPnl(Number(value.pnl));
            }}
            titleForValue={(value) => {
              if (!value) return '';
              const count = Number(value.tradeCount);
              return `${value.date}: ${formatCurrency(Number(value.pnl), currency)} | ${count} ${
                count === 1 ? 'trade' : 'trades'
              }`;
            }}
            onClick={(value) => {
              if (value) onDaySelect(value.date);
            }}
            showWeekdayLabels
          />
        </div>
      </div>
    </section>
  );
}
